import { Color, PaletteMode, Theme, createTheme } from '@mui/material';
import { CSSProperties } from 'react';

//colors
const primaryGreen = '#283E43';
const secondaryBeige = '#FCFBFA';
const darkGreen100 = '#BCC3C5';
const darkGreen200 = '#9CA6A9';
const darkGreen50 = '#EAECEC';
const black300 = '#545454';
const black400 = '#333333';
//fonts
const primaryFont = 'poppins';
const secondaryFont = 'lora';
export const getTheme = (mode: PaletteMode | undefined = 'light') => {
  return mode === 'light' ? theme : darkTheme;
};

const theme: Theme = createTheme({
  palette: {
    primary: {
      main: primaryGreen,
    },
    secondary: {
      main: secondaryBeige,
    },
    background: {
      default: secondaryBeige,
      secondary: darkGreen50,
    },
    green: {
      50: darkGreen50,
      100: darkGreen100,
      200: darkGreen200,
      300: '#6F7E81',
      500: primaryGreen,
      600: '#24383D',
    },
    black: {
      50: '#E6E6E6',
      200: '#8A8A8A',
      300: black300,
      400: black400,
      900: '#000000',
    },
    white: {
      800: '#8C8C8C',
    },
    warning: {
      main: '#FBE3A5',
    },
    text: {
      primary: primaryGreen,
    },
  },
  components: {
    // MuiGrid:{
    //     styleOverrides:{
    //         root:{
    //             "&::-webkit-scrollbar":{
    //                 width: "5px",
    //             },
    //             '&::-webkit-scrollbar-track': {
    //               backgroundColor: 'transparent',
    //             },
    //             '&::-webkit-scrollbar-thumb': {
    //               backgroundColor: black300,
    //               borderRadius: '4px',
    //               paddingRight: 4
    //             },
    //             '&::-webkit-scrollbar-thumb:hover': {
    //               backgroundColor: black400,
    //             },
    //         }
    //     }
    // },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          '&::-webkit-scrollbar': {
            width: '5px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: black300,
            borderRadius: '4px',
            paddingRight: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: black400,
          },
          '.veriff-submit': {
            backgroundColor: primaryGreen,
            borderRadius: '20px',
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: primaryGreen,
            opacity: 1,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          textTransform: 'none',
          boxShadow: 'none',
          '&.Mui-disabled': {
            backgroundColor: 'black.50',
            border: 'none',
            color: 'primary.main',
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: primaryGreen,
            '::before': {
              background: primaryGreen,
              width: '100%',
              height: '100%',
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            background: darkGreen200,
            color: '#FFF',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          background: darkGreen50,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          color: primaryGreen,
        },
        label: {
          fontWeight: 'bold',
        },
        outlined: {
          borderColor: primaryGreen,
        },
      },
    },
    MuiRating: {
      styleOverrides: {
        root: {
          '& .MuiRating-iconFilled': {
            color: primaryGreen,
          },
          '& .MuiRating-iconHover': {
            color: primaryGreen,
          },
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: primaryGreen,
          color: primaryFont,
          fontSize: '12px',
          padding: '8px 16px',
          minWidth: '300px',
        },
        arrow: {
          color: primaryGreen,
        },
      },
    },
  },
  typography: {
    allVariants: {
      color: primaryGreen,
    },
    fontFamily: [primaryFont, secondaryFont].join(','),
    //Poppins
    'display-large': {
      fontSize: '57px',
      fontFamily: primaryFont,
    },
    'display-medium': {
      fontSize: '45px',
      fontFamily: primaryFont,
    },
    'display-small': {
      fontSize: '36px',
      fontFamily: primaryFont,
    },
    'headline-large': {
      fontSize: '32px',
      fontFamily: primaryFont,
    },
    'headline-medium': {
      fontSize: '28px',
      fontFamily: primaryFont,
    },
    'headline-small': {
      fontSize: '24px',
      fontFamily: primaryFont,
    },
    'title-large': {
      fontSize: '22px',
      fontFamily: primaryFont,
    },
    'title-medium': {
      fontSize: '16px',
      fontFamily: primaryFont,
    },
    'title-small': {
      fontSize: '15px',
      fontFamily: primaryFont,
    },
    'label-large': {
      fontSize: '14px',
      fontFamily: primaryFont,
    },
    'label-medium': {
      fontSize: '12px',
      fontFamily: primaryFont,
    },
    'label-small': {
      fontSize: '11px',
      fontFamily: primaryFont,
    },
    'body-large': {
      fontSize: '16px',
      fontFamily: primaryFont,
    },
    'body-medium': {
      fontSize: '14px',
      fontFamily: primaryFont,
    },
    'body-small': {
      fontSize: '12px',
      fontFamily: primaryFont,
    },
    'body-smaller': {
      fontSize: '10px',
      fontFamily: primaryFont,
    },
    //Lora
    'lora-display-large': {
      fontSize: '57px',
      fontFamily: secondaryFont,
    },
    'lora-display-medium': {
      fontSize: '45px',
      fontFamily: secondaryFont,
    },
    'lora-display-small': {
      fontSize: '36px',
      fontFamily: secondaryFont,
    },
    'lora-headline-large': {
      fontSize: '32px',
      fontFamily: secondaryFont,
    },
    'lora-headline-medium': {
      fontSize: '28px',
      fontFamily: secondaryFont,
    },
    'lora-headline-small': {
      fontSize: '24px',
      fontFamily: secondaryFont,
    },
    'lora-title-large': {
      fontSize: '22px',
      fontFamily: secondaryFont,
    },
    'lora-title-medium': {
      fontSize: '16px',
      fontFamily: secondaryFont,
    },
    'lora-title-small': {
      fontSize: '15px',
      fontFamily: secondaryFont,
    },
    'lora-label-large': {
      fontSize: '14px',
      fontFamily: secondaryFont,
    },
    'lora-label-medium': {
      fontSize: '12px',
      fontFamily: secondaryFont,
    },
    'lora-label-small': {
      fontSize: '11px',
      fontFamily: secondaryFont,
    },
    'lora-body-large': {
      fontSize: '16px',
      fontFamily: secondaryFont,
    },
    'lora-body-medium': {
      fontSize: '14px',
      fontFamily: secondaryFont,
    },
    'lora-body-small': {
      fontSize: '12px',
      fontFamily: secondaryFont,
    },
    'lora-body-smaller': {
      fontSize: '10px',
      fontFamily: secondaryFont,
    },
  },
});
const darkTheme: Theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: theme.palette.primary.light,
    },
    secondary: {
      main: theme.palette.secondary.main,
    },
    green: {
      50: darkGreen50,
      100: darkGreen100,
      200: darkGreen200,
      300: '#6F7E81',
      500: primaryGreen,
      600: '#24383D',
    },
    black: {
      50: '#E6E6E6',
      200: '#8A8A8A',
      300: black300,
      400: black400,
      900: '#000000',
    },
    white: {
      800: '#8C8C8C',
    },
    text: {
      primary: theme.palette.primary.light,
    },
    warning: {
      main: '#FBE3A5',
    },
  },
  components: {
    // MuiGrid:{
    //     styleOverrides:{
    //         root:{
    //             "&::-webkit-scrollbar":{
    //                 width: "5px",
    //             },
    //             '&::-webkit-scrollbar-track': {
    //               backgroundColor: 'transparent',
    //             },
    //             '&::-webkit-scrollbar-thumb': {
    //               backgroundColor: black300,
    //               borderRadius: '4px',
    //               paddingRight: 4
    //             },
    //             '&::-webkit-scrollbar-thumb:hover': {
    //               backgroundColor: black400,
    //             },
    //         }
    //     }
    // },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          '&::-webkit-scrollbar': {
            width: '5px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: black300,
            borderRadius: '4px',
            paddingRight: 4,
          },
          '&::-webkit-scrollbar-thumb:hover': {
            backgroundColor: black400,
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          '&::placeholder': {
            color: theme.palette.primary.light,
            opacity: 1,
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '20px',
          textTransform: 'none',
          boxShadow: 'none',
          '&.Mui-disabled': {
            background: '#FFFFFF00',
            border: `1px solid ${theme.palette.primary.light}`,
            color: theme.palette.primary.light,
          },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          '&.Mui-checked': {
            color: primaryGreen,
            '::before': {
              background: primaryGreen,
              width: '100%',
              height: '100%',
            },
          },
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          '&.Mui-selected': {
            background: darkGreen200,
            color: '#FFF',
          },
        },
      },
    },
    MuiAutocomplete: {
      styleOverrides: {
        paper: {
          background: darkGreen50,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '8px',
          color: theme.palette.primary.light,
        },
        label: {
          fontWeight: 'bold',
        },
        outlined: {
          borderColor: theme.palette.primary.light,
        },
      },
    },
  },
  typography: {
    allVariants: {
      color: theme.palette.primary.light,
    },
    fontFamily: [primaryFont, secondaryFont].join(','),
    //Poppins
    'display-large': {
      fontSize: '57px',
      fontFamily: primaryFont,
    },
    'display-medium': {
      fontSize: '45px',
      fontFamily: primaryFont,
    },
    'display-small': {
      fontSize: '36px',
      fontFamily: primaryFont,
    },
    'headline-large': {
      fontSize: '32px',
      fontFamily: primaryFont,
    },
    'headline-medium': {
      fontSize: '28px',
      fontFamily: primaryFont,
    },
    'headline-small': {
      fontSize: '24px',
      fontFamily: primaryFont,
    },
    'title-large': {
      fontSize: '22px',
      fontFamily: primaryFont,
    },
    'title-medium': {
      fontSize: '16px',
      fontFamily: primaryFont,
    },
    'title-small': {
      fontSize: '15px',
      fontFamily: primaryFont,
    },
    'label-large': {
      fontSize: '14px',
      fontFamily: primaryFont,
    },
    'label-medium': {
      fontSize: '12px',
      fontFamily: primaryFont,
    },
    'label-small': {
      fontSize: '11px',
      fontFamily: primaryFont,
    },
    'body-large': {
      fontSize: '16px',
      fontFamily: primaryFont,
    },
    'body-medium': {
      fontSize: '14px',
      fontFamily: primaryFont,
    },
    'body-small': {
      fontSize: '12px',
      fontFamily: primaryFont,
    },
    'body-smaller': {
      fontSize: '10px',
      fontFamily: primaryFont,
    },
    //Lora
    'lora-display-large': {
      fontSize: '57px',
      fontFamily: secondaryFont,
    },
    'lora-display-medium': {
      fontSize: '45px',
      fontFamily: secondaryFont,
    },
    'lora-display-small': {
      fontSize: '36px',
      fontFamily: secondaryFont,
    },
    'lora-headline-large': {
      fontSize: '32px',
      fontFamily: secondaryFont,
    },
    'lora-headline-medium': {
      fontSize: '28px',
      fontFamily: secondaryFont,
    },
    'lora-headline-small': {
      fontSize: '24px',
      fontFamily: secondaryFont,
    },
    'lora-title-large': {
      fontSize: '22px',
      fontFamily: secondaryFont,
    },
    'lora-title-medium': {
      fontSize: '16px',
      fontFamily: secondaryFont,
    },
    'lora-title-small': {
      fontSize: '15px',
      fontFamily: secondaryFont,
    },
    'lora-label-large': {
      fontSize: '14px',
      fontFamily: secondaryFont,
    },
    'lora-label-medium': {
      fontSize: '12px',
      fontFamily: secondaryFont,
    },
    'lora-label-small': {
      fontSize: '11px',
      fontFamily: secondaryFont,
    },
    'lora-body-large': {
      fontSize: '16px',
      fontFamily: secondaryFont,
    },
    'lora-body-medium': {
      fontSize: '14px',
      fontFamily: secondaryFont,
    },
    'lora-body-small': {
      fontSize: '12px',
      fontFamily: secondaryFont,
    },
    'lora-body-smaller': {
      fontSize: '10px',
      fontFamily: secondaryFont,
    },
  },
});

declare module '@mui/material/styles/createPalette' {
  interface Palette {
    black: Color;
    green: Color;
    white: Color;
  }

  interface TypeBackground {
    secondary: string;
  }

  interface PaletteOptions {
    black: ColorPartial;
    green: ColorPartial;
    white: ColorPartial;
  }
}

declare module '@mui/material/styles' {
  interface TypographyVariants {
    //Poppins
    'display-large': CSSProperties;
    'display-medium': CSSProperties;
    'display-small': CSSProperties;
    'headline-large': CSSProperties;
    'headline-medium': CSSProperties;
    'headline-small': CSSProperties;
    'title-large': CSSProperties;
    'title-medium': CSSProperties;
    'title-small': CSSProperties;
    'label-large': CSSProperties;
    'label-medium': CSSProperties;
    'label-small': CSSProperties;
    'body-large': CSSProperties;
    'body-medium': CSSProperties;
    'body-small': CSSProperties;
    'body-smaller': CSSProperties;
    'outlined-disabled': CSSProperties;

    //Lora
    'lora-display-large': CSSProperties;
    'lora-display-medium': CSSProperties;
    'lora-display-small': CSSProperties;
    'lora-headline-large': CSSProperties;
    'lora-headline-medium': CSSProperties;
    'lora-headline-small': CSSProperties;
    'lora-title-large': CSSProperties;
    'lora-title-medium': CSSProperties;
    'lora-title-small': CSSProperties;
    'lora-label-large': CSSProperties;
    'lora-label-medium': CSSProperties;
    'lora-label-small': CSSProperties;
    'lora-body-large': CSSProperties;
    'lora-body-medium': CSSProperties;
    'lora-body-small': CSSProperties;
    'lora-body-smaller': CSSProperties;
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    //Poppins
    'display-large'?: CSSProperties;
    'display-medium'?: CSSProperties;
    'display-small'?: CSSProperties;
    'headline-large'?: CSSProperties;
    'headline-medium'?: CSSProperties;
    'headline-small'?: CSSProperties;
    'title-large'?: CSSProperties;
    'title-medium'?: CSSProperties;
    'title-small'?: CSSProperties;
    'label-large'?: CSSProperties;
    'label-medium'?: CSSProperties;
    'label-small'?: CSSProperties;
    'body-large'?: CSSProperties;
    'body-medium'?: CSSProperties;
    'body-small'?: CSSProperties;
    'body-smaller'?: CSSProperties;

    //Lora
    'lora-display-large'?: CSSProperties;
    'lora-display-medium'?: CSSProperties;
    'lora-display-small'?: CSSProperties;
    'lora-headline-large'?: CSSProperties;
    'lora-headline-medium'?: CSSProperties;
    'lora-headline-small'?: CSSProperties;
    'lora-title-large'?: CSSProperties;
    'lora-title-medium'?: CSSProperties;
    'lora-title-small'?: CSSProperties;
    'lora-label-large'?: CSSProperties;
    'lora-label-medium'?: CSSProperties;
    'lora-label-small'?: CSSProperties;
    'lora-body-large'?: CSSProperties;
    'lora-body-medium'?: CSSProperties;
    'lora-body-small'?: CSSProperties;
    'lora-body-smaller'?: CSSProperties;
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    //Poppins
    'display-large': true;
    'display-medium': true;
    'display-small': true;
    'headline-large': true;
    'headline-medium': true;
    'headline-small': true;
    'title-large': true;
    'title-medium': true;
    'title-small': true;
    'label-large': true;
    'label-medium': true;
    'label-small': true;
    'body-large': true;
    'body-medium': true;
    'body-small': true;
    'body-smaller': true;

    //Lora
    'lora-display-large': true;
    'lora-display-medium': true;
    'lora-display-small': true;
    'lora-headline-large': true;
    'lora-headline-medium': true;
    'lora-headline-small': true;
    'lora-title-large': true;
    'lora-title-medium': true;
    'lora-title-small': true;
    'lora-label-large': true;
    'lora-label-medium': true;
    'lora-label-small': true;
    'lora-body-large': true;
    'lora-body-medium': true;
    'lora-body-small': true;
    'lora-body-smaller': true;
  }
}

declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    'outlined-disabled': true;
  }
}
